import { Link } from "react-router-dom";
import { Avatar } from "@/components/ui/Display.jsx";
import { formatTime } from "@/lib/format.js";
import { cx } from "@/lib/cx.js";

/*
 * The "who is at their desk right now" grid, from dashboard.html.
 *
 * Ring colour is the presence bucket and the legend sits UNDER the grid, as in
 * the markup — three shades of ring with no key is a puzzle, and the difference
 * between "away" and "not working at all" is the one an admin acts on.
 *
 * The last-seen time is right-aligned at the end of the row so the times form a
 * column you can scan, instead of hiding under each name.
 */
const RING = {
    active: "ring-emerald-500",
    waiting: "ring-amber-500",
    away: "ring-slate-300 dark:ring-slate-600",
};

const DOT = {
    active: "bg-emerald-500",
    waiting: "bg-amber-500",
    away: "bg-slate-400",
};

const LEGEND = [
    ["active", "Active — input in last 5 min"],
    ["waiting", "Waiting — 5–10 min idle"],
    ["away", "Away — 10+ min idle"],
];

export default function CurrentlyWorking({ people = [] }) {
    if (!people.length) {
        return (
            <p className="py-8 text-center text-[13px] text-slate-400 dark:text-slate-500">
                Nobody is tracking right now.
            </p>
        );
    }

    return (
        <>
            <div className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                {people.map((person) => (
                    <Link
                        key={person._id}
                        to={`/dashboard/${person._id}`}
                        className="flex min-w-0 items-center gap-2.5 rounded-lg px-2 py-2 transition hover:bg-slate-50 dark:hover:bg-slate-800/60"
                    >
                        <Avatar
                            name={person.name}
                            size="sm"
                            ring={cx(
                                "ring-2 ring-offset-2 ring-offset-white dark:ring-offset-slate-900",
                                RING[person.presence],
                            )}
                        />

                        <span className="min-w-0 flex-1 truncate text-[13px] font-medium">
                            {person.name}
                        </span>

                        {person.presence !== "active" && (
                            <span
                                className={cx(
                                    "shrink-0 font-mono text-[11.5px]",
                                    person.presence === "waiting"
                                        ? "text-amber-600 dark:text-amber-400"
                                        : "text-slate-400 dark:text-slate-500",
                                )}
                            >
                                {formatTime(person.lastActivity)}
                            </span>
                        )}
                    </Link>
                ))}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-slate-100 pt-3.5 dark:border-slate-800">
                {LEGEND.map(([key, label]) => (
                    <span
                        key={key}
                        className="flex items-center gap-1.5 text-[11.5px] text-slate-500 dark:text-slate-400"
                    >
                        <span className={cx("size-2 shrink-0 rounded-full", DOT[key])} />
                        {label}
                    </span>
                ))}
            </div>
        </>
    );
}
